import React, { useState } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { aiExpressionAuthoringApi } from 'app/services/api';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  EXPLAIN_EMPTY_EXPRESSION_HELPER,
  EXPLAIN_ERROR_HELPER,
  EXPLAIN_INLINE_SCHEMA_DOMAIN,
  EXPLAIN_LOADING_HELPER,
  EXPLAIN_READS_LABEL,
  EXPLAIN_WATCH_OUT_LABEL,
} from './ExplainExpression.constants';
import {
  modalBodyWrapperStyles,
  modalSectionLabelStyles,
  modalStatusRowStyles,
  watchOutWrapperStyles,
} from './ExplainExpression.styles';
import { ExplainExpressionTestIds } from './ExplainExpression.testIds';
import { EXPLAIN_STATUS, isCancelledError } from './ExplainExpression.utils';
import ExplainMarkdownText from './ExplainMarkdownText';

const ExplainExpressionModalBody = ({
  expressionJson,
  schemaDomain = EXPLAIN_INLINE_SCHEMA_DOMAIN,
  expressionText = null,
  schemaJson = null,
}) => {
  const initialStatus = _.isEmpty(expressionJson) ? EXPLAIN_STATUS.EMPTY : EXPLAIN_STATUS.LOADING;

  const [status, setStatus] = useState(initialStatus);
  const [result, setResult] = useState(null);

  useEffectSafeStrictMode(() => {
    if (_.isEmpty(expressionJson)) {
      setStatus(EXPLAIN_STATUS.EMPTY);

      return _.noop;
    }

    const controller = new AbortController();

    setStatus(EXPLAIN_STATUS.LOADING);
    setResult(null);

    aiExpressionAuthoringApi
      .explainExpression({
        schemaDomain,
        expressionJson,
        expressionText,
        schemaJson,
        signal: controller.signal,
      })
      .then(({ data }) => {
        setResult(data);
        setStatus(EXPLAIN_STATUS.SUCCESS);
      })
      .catch(error => {
        if (isCancelledError(error)) {
          return;
        }

        setStatus(EXPLAIN_STATUS.ERROR);
      });

    return () => controller.abort();
  }, [schemaDomain, expressionJson, expressionText, schemaJson]);

  const isSuccess = status === EXPLAIN_STATUS.SUCCESS;
  const hasReads = isSuccess && !_.isEmpty(_.trim(result?.reads));
  const hasWatchOut = isSuccess && !_.isEmpty(_.trim(result?.watchOut));

  return (
    <Box data-testid={ExplainExpressionTestIds.MODAL_BODY} sx={modalBodyWrapperStyles}>
      {status === EXPLAIN_STATUS.EMPTY && (
        <Box sx={modalStatusRowStyles}>
          <Typography color="text.secondary" variant="regular14">
            {EXPLAIN_EMPTY_EXPRESSION_HELPER}
          </Typography>
        </Box>
      )}

      {status === EXPLAIN_STATUS.LOADING && (
        <Box data-testid={ExplainExpressionTestIds.MODAL_LOADING} sx={modalStatusRowStyles}>
          <CircularProgress size={20} />
          <Typography color="text.secondary" variant="regular14">
            {EXPLAIN_LOADING_HELPER}
          </Typography>
        </Box>
      )}

      {status === EXPLAIN_STATUS.ERROR && (
        <Box data-testid={ExplainExpressionTestIds.MODAL_ERROR} sx={modalStatusRowStyles}>
          <Typography color="error.main" variant="regular14">
            {EXPLAIN_ERROR_HELPER}
          </Typography>
        </Box>
      )}

      {isSuccess && (
        <Typography
          component="div"
          data-testid={ExplainExpressionTestIds.MODAL_SUMMARY}
          variant="regular14"
        >
          <ExplainMarkdownText text={result?.summary} />
        </Typography>
      )}

      {hasReads && (
        <Box>
          <Typography color="text.secondary" sx={modalSectionLabelStyles} variant="bold12">
            {EXPLAIN_READS_LABEL}
          </Typography>
          <Typography
            component="div"
            data-testid={ExplainExpressionTestIds.MODAL_READS}
            variant="regular14"
          >
            <ExplainMarkdownText text={result.reads} />
          </Typography>
        </Box>
      )}

      {hasWatchOut && (
        <Box sx={watchOutWrapperStyles}>
          <Typography color="warning.dark" sx={modalSectionLabelStyles} variant="bold12">
            {EXPLAIN_WATCH_OUT_LABEL}
          </Typography>
          <Typography
            color="warning.dark"
            component="div"
            data-testid={ExplainExpressionTestIds.MODAL_WATCH_OUT}
            variant="regular14"
          >
            <ExplainMarkdownText text={result.watchOut} />
          </Typography>
        </Box>
      )}
    </Box>
  );
};

ExplainExpressionModalBody.propTypes = {
  expressionJson: PropTypes.string.isRequired,
  schemaDomain: PropTypes.string,
  expressionText: PropTypes.string,
  schemaJson: PropTypes.string,
};

export default ExplainExpressionModalBody;
